import React from 'react';
import { DatePicker } from '@maxtropy/components';
import { PickerMode } from 'rc-picker/es/interface';
import dayjs, { Dayjs, ManipulateType, OpUnitType, QUnitType } from 'dayjs';
import styles from './index.module.scss';
import { RangePickerProps } from 'antd/es/date-picker/generatePicker/interface';

const { RangePicker } = DatePicker;

type RangeValue = [Dayjs | null, Dayjs | null] | null;

export type RangePickerValue = RangePickerProps<dayjs.Dayjs>['value'];

export enum DatePickerType {
  DAY = 'date',
  MONTH = 'month',
  YEAR = 'year',
}

export enum OriginalType {
  ORIGINAL = 'original',
  ORIGINALENUM = 'originalenum',
}

export type StatisticsPartition = DatePickerType | OriginalType;

export const partitionToPicker: { [K: string]: PickerMode } = {
  [DatePickerType.DAY]: 'date',
  [DatePickerType.MONTH]: 'month',
  [DatePickerType.YEAR]: 'year',
  [OriginalType.ORIGINAL]: 'date',
  [OriginalType.ORIGINALENUM]: 'date',
};

export interface DateSwitchProps {
  mode: StatisticsPartition;
  rangePickerValue: RangePickerValue | any;
  handleRangePickerChange: (dates: RangePickerValue, dateStrings: [string, string]) => void;
}

interface DateLimitType {
  name: StatisticsPartition;
  unitOfTime: QUnitType | OpUnitType;
  unit: ManipulateType;
  num: number;
}

const dateLimit: DateLimitType[] = [
  {
    name: DatePickerType.DAY,
    unitOfTime: 'days',
    unit: 'day',
    num: 30,
  },
  {
    name: DatePickerType.MONTH,
    unitOfTime: 'months',
    unit: 'month',
    num: 11,
  },
  {
    name: DatePickerType.YEAR,
    unitOfTime: 'years',
    unit: 'year',
    num: 9,
  },
  {
    name: OriginalType.ORIGINAL,
    unitOfTime: 'days',
    unit: 'day',
    num: 5,
  },
  {
    name: OriginalType.ORIGINALENUM,
    unitOfTime: 'days',
    unit: 'day',
    num: 5,
  },
];

export const getDisabledDateByMode =
  (mode: StatisticsPartition, limitOverride?: Partial<Record<StatisticsPartition, number>>) =>
  (current: Dayjs, { from }: { from?: Dayjs }) => {
    const isFuture = current >= dayjs().endOf('day');

    const limitConfig = dateLimit.find(item => item.name === mode);
    if (!limitConfig) return isFuture;
    const { unitOfTime, unit } = limitConfig;
    const num = limitOverride?.[mode] ?? limitConfig.num;
    const currentAligned = current.startOf(unit);

    if (from) {
      const fromAligned = from.startOf(unit);
      return Math.abs(currentAligned.diff(fromAligned, unitOfTime)) > num || isFuture;
    }
    return isFuture;
  };

export const getTimeDistance = (mode: StatisticsPartition): RangePickerValue => {
  switch (mode) {
    case DatePickerType.DAY:
      return [dayjs().subtract(6, 'day'), dayjs()];
    case DatePickerType.MONTH:
      return [dayjs().subtract(5, 'month'), dayjs()];
    case DatePickerType.YEAR:
      return [dayjs().subtract(4, 'year'), dayjs()];
    case OriginalType.ORIGINAL:
      return [dayjs(), dayjs()];
    case OriginalType.ORIGINALENUM:
      return [dayjs(), dayjs()];
  }
};

export const getTsRange = (date: RangePickerValue, mode: StatisticsPartition) => {
  if (!date) {
    return {};
  } else {
    switch (mode) {
      case OriginalType.ORIGINAL:
      case OriginalType.ORIGINALENUM:
        let tsEnd;
        if (dayjs().isSame(dayjs(date[1]), 'day')) {
          tsEnd = dayjs().valueOf();
        } else {
          tsEnd = dayjs(date[1]).endOf('day').valueOf();
        }
        return {
          tsStart: dayjs(date[0]).startOf('day').valueOf(),
          tsEnd,
        };
      case DatePickerType.DAY:
        return {
          tsStart: dayjs(date[0]).startOf('day').valueOf(),
          tsEnd: dayjs(date[1]).endOf('day').valueOf(),
        };
      case DatePickerType.MONTH:
        return {
          tsStart: dayjs(date[0]).startOf('month').valueOf(),
          tsEnd: dayjs(date[1]).endOf('month').valueOf(),
        };
      case DatePickerType.YEAR:
        return {
          tsStart: dayjs(date[0]).startOf('year').valueOf(),
          tsEnd: dayjs(date[1]).endOf('year').valueOf(),
        };
      default:
        return {};
    }
  }
};

export const getColumnConfig = (mode: StatisticsPartition) => {
  switch (mode) {
    case DatePickerType.DAY:
      return {
        name: '日期',
        dateFormat: 'YYYY-MM-DD',
      };
    case DatePickerType.MONTH:
      return {
        name: '月份',
        dateFormat: 'YYYY-MM',
      };
    case DatePickerType.YEAR:
      return {
        name: '年份',
        dateFormat: 'YYYY',
      };
    case OriginalType.ORIGINAL:
    case OriginalType.ORIGINALENUM:
      return {
        name: '日期',
        dateFormat: 'YYYY-MM-DD HH:mm:ss',
      };
    default:
      return {};
  }
};

const DateSwitch: React.FC<DateSwitchProps> = props => {
  const { mode, rangePickerValue, handleRangePickerChange } = props;

  return (
    <div className={styles.pickerArea}>
      <div className={styles.label}>统计时间</div>

      <RangePicker
        style={{ width: 294 }}
        allowClear={false}
        picker={partitionToPicker[mode]}
        onChange={handleRangePickerChange}
        value={rangePickerValue}
        disabledDate={getDisabledDateByMode(mode)}
      />
    </div>
  );
};

export default DateSwitch;
